import getUserLocale from 'get-user-locale'
// Import all translation files
import enTranslations from '../locales/en.json'
import csTranslations from '../locales/cs.json'
import skTranslations from '../locales/sk-SK.json'
import deTranslations from '../locales/de-AT.json'
import roTranslations from '../locales/ro-RO.json'

// Type definition for translations
type TranslationKey = string
type TranslationObject = Record<string, any>

// SessionStorage key for the shop's primary locale (set by Auth.tsx after login)
export const SHOP_LOCALE_KEY = 'shopLocale'

// Available translations
const translations: Record<string, TranslationObject> = {
  en: enTranslations,
  cs: csTranslations,
  'cs-CZ': csTranslations,
  sk: skTranslations,
  'sk-SK': skTranslations,
  de: deTranslations,
  'de-AT': deTranslations,
  ro: roTranslations,
  'ro-RO': roTranslations,
}

/**
 * Tries to match a locale string against available translations.
 * First tries exact match, then language-only (e.g., 'cs-CZ' → 'cs').
 */
function resolveLocale(locale: string): string | null {
  if (translations[locale]) {
    return locale
  }
  const lang = locale.split('-')[0]
  if (translations[lang]) {
    return lang
  }
  return null
}

/**
 * Returns the best locale for the app UI.
 *
 * Priority:
 *   1. Shop's primary locale from Shopify API (stored in sessionStorage by Auth.tsx)
 *   2. Browser locale (navigator.language via get-user-locale)
 *   3. English fallback
 */
export function getAppLocale(): string {
  // 1. Shop's primary locale from Shopify API
  try {
    const shopLocale = window.sessionStorage.getItem(SHOP_LOCALE_KEY)
    if (shopLocale) {
      const resolved = resolveLocale(shopLocale)
      if (resolved) return resolved
    }
  } catch {
    // sessionStorage not available (e.g., SSR or privacy mode)
  }

  // 2. Browser locale
  const browserLocale = getUserLocale()
  const resolved = resolveLocale(browserLocale)
  if (resolved) return resolved

  // 3. English fallback
  return 'en'
}

export function getTranslation(key: TranslationKey, variables?: Record<string, string>): string {
  const locale = getAppLocale()

  const translationObj = translations[locale] || translations['en']

  // Split the key by dots to navigate nested objects
  const keys = key.split('.')
  let value: any = translationObj

  // Navigate through the nested object
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      // Fallback to English if key not found in current locale
      if (locale !== 'en') {
        let fallbackValue: any = translations['en']
        for (const fallbackKey of keys) {
          if (fallbackValue && typeof fallbackValue === 'object' && fallbackKey in fallbackValue) {
            fallbackValue = fallbackValue[fallbackKey]
          } else {
            // Return the key itself if not found in fallback either
            return key
          }
        }
        value = fallbackValue
      } else {
        // Return the key itself if not found
        return key
      }
      break
    }
  }

  // If final value is not a string, return the key
  if (typeof value !== 'string') {
    return key
  }

  // Interpolate variables if provided
  if (variables) {
    return value.replace(/\{(\w+)\}/g, (match, varName) => {
      return variables[varName] || match
    })
  }

  return value
}
